import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface RecruitingTeamSectionProps {
  currentRecruiters: {
    tech: number;
    gtm: number;
    gna: number;
    exec: number;
  };
  fteHeadcount: number;
  outsourcedHeadcount: number;
  onChange: (field: string) => (value: number) => void;
  isCalculated?: boolean;
}

export const RecruitingTeamSection = ({
  currentRecruiters,
  fteHeadcount,
  outsourcedHeadcount,
  onChange,
  isCalculated = false,
}: RecruitingTeamSectionProps) => {
  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow any numeric input including empty string and zero
    if (value === '' || (!isNaN(Number(value)) && Number(value) >= 0)) {
      const numericValue = value === '' ? 0 : Number(value);
      
      // Map the input IDs to the correct currentRecruiters field
      const fieldMapping: { [key: string]: string } = {
        currentTechRecruiters: "tech",
        currentGtmRecruiters: "gtm",
        currentGnaRecruiters: "gna",
        currentExecRecruiters: "exec",
      };

      if (field in fieldMapping) {
        console.log(`Updating currentRecruiters.${fieldMapping[field]} to:`, numericValue);
        onChange(`currentRecruiters.${fieldMapping[field]}`)(numericValue);
      } else {
        console.log(`Updating ${field} to:`, numericValue);
        onChange(field)(numericValue);
      }
    }
  };

  // Format value for display - return empty string for 0 only in display
  const formatDisplayValue = (value: number) => value === 0 && !String(value).length ? '' : String(value);

  return (
    <div className="space-y-6">
      <h3 className="font-semibold text-lg">Recruiting Team</h3>
      
      {/* Current Recruiting Team Section */}
      <div className="space-y-4 border-b pb-6">
        <h4 className="font-medium">Current Recruiting Team</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="currentTechRecruiters">Tech & Product Recruiters</Label>
            <Input
              id="currentTechRecruiters"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={formatDisplayValue(currentRecruiters.tech)}
              onChange={handleInputChange("currentTechRecruiters")}
              required
              readOnly={isCalculated}
              className={isCalculated ? "bg-gray-100" : ""}
              placeholder="Enter number"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="currentGtmRecruiters">Go-to-market Recruiters</Label>
            <Input
              id="currentGtmRecruiters"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={formatDisplayValue(currentRecruiters.gtm)}
              onChange={handleInputChange("currentGtmRecruiters")}
              required
              readOnly={isCalculated}
              className={isCalculated ? "bg-gray-100" : ""}
              placeholder="Enter number"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="currentGnaRecruiters">G&A Recruiters</Label>
            <Input
              id="currentGnaRecruiters"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={formatDisplayValue(currentRecruiters.gna)}
              onChange={handleInputChange("currentGnaRecruiters")}
              required
              readOnly={isCalculated}
              className={isCalculated ? "bg-gray-100" : ""}
              placeholder="Enter number"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="currentExecRecruiters">Executive Recruiters</Label>
            <Input
              id="currentExecRecruiters"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={formatDisplayValue(currentRecruiters.exec)}
              onChange={handleInputChange("currentExecRecruiters")}
              required
              readOnly={isCalculated}
              className={isCalculated ? "bg-gray-100" : ""}
              placeholder="Enter number"
            />
          </div>
        </div>
      </div>

      {/* Target Recruiting Team Composition Section */}
      <div className="space-y-4">
        <h4 className="font-medium">Target Recruiting Team Composition</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="fteHeadcount">FTE Headcount (%)</Label>
            <Input
              id="fteHeadcount"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={formatDisplayValue(fteHeadcount)}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || (!isNaN(Number(value)) && Number(value) >= 0 && Number(value) <= 100)) {
                  const numericValue = value === '' ? 0 : Number(value);
                  onChange("fteHeadcount")(numericValue);
                  // Automatically update outsourced headcount
                  onChange("outsourcedHeadcount")(100 - numericValue);
                }
              }}
              required
              readOnly={isCalculated}
              className={isCalculated ? "bg-gray-100" : ""}
              placeholder="Enter percentage"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="outsourcedHeadcount">Outsourced Headcount (%)</Label>
            <Input
              id="outsourcedHeadcount"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={formatDisplayValue(outsourcedHeadcount)}
              className="bg-gray-100"
              placeholder="Automatically calculated"
              readOnly
            />
          </div>
        </div>
      </div>
    </div>
  );
};